import { useCallback, useEffect, useRef } from 'react';

import { type RovoMemoryEditsResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

interface MemoryEditItem {
	id: string;
	content: string;
	createdAt: string;
}

export interface UseMemoryEditsProps extends AssistanceServiceInjectedParams {
	/**
	 * Whether to enable fetching
	 */
	isEnabled: boolean;
	/** Called after a memory edit is successfully deleted. */
	onDeleteSuccess?: () => void;
}

export interface UseMemoryEditsReturn {
	/**
	 * The list of pending memory edits
	 */
	edits: MemoryEditItem[];
	/**
	 * Whether memory edits are currently loading
	 */
	isLoading: boolean;
	/**
	 * Function to retry fetching memory edits
	 */
	onRetry: () => Promise<void>;
	/**
	 * Function to delete a memory edit by ID
	 */
	onDeleteEdit: (editId: string) => Promise<void>;
	/**
	 * Whether there was an error fetching memory edits
	 */
	error?: ApiError | null;
	/**
	 * Refetch the list of memory edits — useful for parent hooks to trigger after a create
	 */
	refetch: () => Promise<void>;
}

/**
 * Shared hook for fetching and creating memory edits.
 * Handles fetching the list of pending memory edits, creating new edits,
 * deleting edits, and error/loading states — all via the assistance service.
 */
export const useMemoryEdits: (
	props: Omit<UseMemoryEditsProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => UseMemoryEditsReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		isEnabled,
		onDeleteSuccess,
	}: UseMemoryEditsProps): UseMemoryEditsReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;

		// Fetch memory edits
		const {
			data: editsResponse,
			isFetching,
			refetch,
			error,
		} = useQuery<RovoMemoryEditsResponse>({
			queryKey: ['rovo-memory-edits'],
			enabled: isEnabled,
			queryFn: assistanceService.getRovoMemoryEdits,
		});

		useEffect(() => {
			if (isEnabled) {
				refetch();
			}
		}, [isEnabled, refetch]);

		const edits: MemoryEditItem[] = editsResponse?.items ?? [];

		const handleRetry = useCallback(async () => {
			await refetch();
		}, [refetch]);

		const onDeleteSuccessRef = useRef(onDeleteSuccess);
		onDeleteSuccessRef.current = onDeleteSuccess;

		// Mutation for deleting a memory edit
		const { mutate: deleteEdit } = useMutation<string, void>({
			mutationFn: async (editId: string) => {
				return assistanceService.deleteRovoMemoryEdit(editId);
			},
			onSuccess: async () => {
				await refetch();
				onDeleteSuccessRef.current?.();
			},
		});

		return {
			edits,
			isLoading: isFetching,
			onRetry: handleRetry,
			onDeleteEdit: deleteEdit,
			error,
			refetch: handleRetry,
		};
	},
);
